import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { GrpcFactoryService } from '../shared/sharedServices/grpc-factory.service';
import { UserCompanyControllerClient } from '../shared/api/ph-web/user/user.apiServiceClientPb';
import {
  GetUsersSegmentRequest,
  GetUsersSegmentResponse,
  RegisterUserCompanyRequest,
  RegisterUserRequest,
  RegisterUserResponse,
  StatusInfo,
} from '../shared/api/ph-web/user/user.api_pb';
import { UserToCreate } from './models/interface/userToCreate.interface';
import { CompanyToCreate } from './models/interface/companyToCreate.interface';
import { ItemNumberToFilter } from './models/interface/itemNumberToFilter.interface';
import { TranslateService } from '@ngx-translate/core';
import { UtilsService } from '../shared/sharedServices/utils.service';
import { Country } from './models/interface/country.interface';
import { MuException } from '../shared/types/exception.type';
import { MuExceptionCode } from '../shared/models/interface/muExceptionCode.enum';
import { MuHttpService } from '../shared/sharedServices/mu-http.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userCompanyClient = this.grpcFactoryService.create(
    UserCompanyControllerClient,
    `${environment.hostname}${environment.hostPort}`,
    'UserCompanyControllerClient'
  );

  private registerUserCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.registerUser, {
    callName: 'registerUser',
  });
  private registerCompanyCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.registerUserCompany, {
    callName: 'registerUserCompany',
  });
  private getSegmentationListCall = this.grpcFactoryService.call(this.userCompanyClient, this.userCompanyClient.getUsersSegment, {
    callName: 'getUsersSegment',
  });

  constructor(
    private grpcFactoryService: GrpcFactoryService,
    private translateService: TranslateService,
    private utilsService: UtilsService,
    private Muhttp: MuHttpService
  ) {
    /** */
  }

  /**
   * regiser person user
   *
   * @param user
   */
  async registerPerson(user: UserToCreate): Promise<boolean> {
    const request: RegisterUserRequest = new RegisterUserRequest();
    request.setEmail(user.email);
    request.setPassword(user.password);
    request.setName(user.name);
    request.setDocumenttype(user.documentType);
    request.setDocument(user.document);
    request.setCellphone(user.cellPhone);
    request.setRolesList([user.roleId]);
    request.setCity(user.cityId);
    const response: RegisterUserResponse = await this.registerUserCall(request);
    if (response.getStatusinfo().getStatus() === StatusInfo.StatusType.ERROR) {
      throw new MuException({
        code: MuExceptionCode.COMMON,
        message: response?.getStatusinfo()?.getMessage(),
      });
    }
    return true;
  }

  /**
   * register company
   *
   * @param company
   */
  async registerCompany(company: CompanyToCreate): Promise<boolean> {
    const request: RegisterUserCompanyRequest = new RegisterUserCompanyRequest();
    request.setContactname(company.contactName);
    request.setEmail(company.email);
    request.setPassword(company.password);
    request.setBusinessname(company.businessName);
    request.setDocumenttype(company.documentType);
    request.setDocument(company.documentNumber);
    request.setVerificationdigit(company.verificationCode);
    request.setPhone(company.phone);
    request.setCellphone(company.cellPhone);
    request.setCityid(company.cityId);
    request.setRole(company.roleId);
    request.setZipcode(company.zipCode);
    request.setSegmentid(company.segmentationId);
    const response = await this.registerCompanyCall(request);
    if (response.getStatusinfo().getStatus() === StatusInfo.StatusType.ERROR) {
      throw new MuException({
        code: MuExceptionCode.COMMON,
        message: response.getStatusinfo().getMessage(),
      });
    }
    return true;
  }

  /**
   * get segmentationList
   * @returns {ItemNumberToFilter[]} response
   */
  async getSegmentationList(country: Country): Promise<ItemNumberToFilter[]> {
    const request: GetUsersSegmentRequest = new GetUsersSegmentRequest();
    const response: GetUsersSegmentResponse = await this.getSegmentationListCall(request);
    return response.getUsersegmentsList().map(segment => {
      return {
        value: segment.getId(),
        label: this.translateService.instant('rangeMinMax', {
          min: this.utilsService.formatCurrency(segment.getMinmonthlydeliveries(), country).replace('$', '').replace(/\.00$/, ''),
          max: this.utilsService.formatCurrency(segment.getMaxmonthlydeliveries(), country).replace('$', '').replace(/\.00$/, ''),
        }),
      };
    });
  }
}
